import { selectPOIs } from "@/features/survey/poisSelectors";
import { reorderPOIs, setPOIs } from "@/features/survey/poisSlice";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { DndContext } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldButtonVariant } from "@emerald/nxcore";
import { EmptyState } from "@emerald-react/empty-state";
import useSimpleConfirmation from "@hooks/useSimpleConfirmation";
import { useSortableList } from "@hooks/useSortable";
import useSurveyState from "@hooks/useSurveyState";
import {
  useBulkImportPOIsMutation,
  useCreatePOIMutation,
  useImportPOIsFromExcelMutation,
  useParsePOIsMutation,
  useReorderPOIMutation,
} from "@services/survey-poi";
import { currentPoiId as currentPoiIdAction } from "@survey/surveySlice";
import RowReportModal from "@survey-properties/RowReportModal";
import React, { useEffect, useRef, useState } from "react";
import POIColumnMatchingModal from "./POIColumnMatchingModal";
import PoiListItem from "./PoiListItem";

const PoisListDrawer = () => {
  const dispatch = useAppDispatch();
  const { survey } = useSurveyState();
  const surveyId = survey?.id ?? "";
  const fileInputRef = useRef<HTMLInputElement>(null);

  const pois = useAppSelector(selectPOIs);

  // Upload flow state
  const [showColumnMatching, setShowColumnMatching] = useState(false);
  const [showRowReport, setShowRowReport] = useState(false);
  const [importResult, setImportResult] = useState<{
    matched: string[];
    notMatched: string[];
    unParsedColumns: string[][];
  } | null>(null);
  const [importSummary, setImportSummary] = useState<{
    toImport: number;
    notImported: number;
    totalProperties: number;
    geocodingErrors?: number;
  } | null>(null);
  const [parsedPOIsData, setParsedPOIsData] = useState<any>(null);

  // Tracks when we're programmatically transitioning from column matching → row report
  const isTransitioningToRowReport = useRef(false);
  const lastParsedRequestId = useRef<string | undefined>(undefined);

  // POI categories are fetched at Builder.tsx level to avoid duplicate requests
  // This component reads from Redux state as needed (see selectPOICategories if needed)

  const [createPOI] = useCreatePOIMutation();
  const [reorderPOI] = useReorderPOIMutation();
  const [importPOIsFromExcel, { isLoading: isUploading }] =
    useImportPOIsFromExcelMutation();
  const [parsePOIs, parseResult] = useParsePOIsMutation();
  const [bulkImportPOIs, { isLoading: isImporting }] =
    useBulkImportPOIsMutation();
  const { withLoading } = useSimpleConfirmation();

  const handleReorder = (
    _reorderedItems: unknown[],
    sourceIndex: number,
    destinationIndex: number,
  ) => {
    if (pois.length === 0) return;

    dispatch(reorderPOIs({ sourceIndex, destinationIndex }));

    reorderPOI({
      surveyId,
      poiId: pois[sourceIndex]?.extId ?? "",
      order: destinationIndex,
    }).catch((error) => {
      console.error("Failed to reorder POI:", error);
      dispatch(reorderPOIs({ sourceIndex: destinationIndex, destinationIndex: sourceIndex }));
    });
  };

  const { DndContextProps, SortableContextProps } = useSortableList({
    items: pois as any,
    onReorder: handleReorder,
  });

  const handleAddPoi = async () => {
    if (!surveyId) return;

    try {
      const result = await createPOI({
        surveyId,
        body: {
          addressLine1: "",
          city: "",
          state: "",
        },
      }).unwrap();

      // Set as current POI to open the form
      dispatch(currentPoiIdAction({ poiId: result.extId }));
    } catch (error) {
      console.error("Failed to create POI:", error);
    }
  };

  const handleUploadFromExcel = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (
      !file.name.toLowerCase().endsWith(".xlsx") &&
      !file.name.toLowerCase().endsWith(".xls")
    ) {
      alert("Please select a valid Excel file (.xlsx or .xls)");
      return;
    }

    // Reset all import state before starting a new import
    setImportResult(null);
    setImportSummary(null);
    setParsedPOIsData(null);
    setShowColumnMatching(false);
    setShowRowReport(false);

    if (parseResult.reset) {
      parseResult.reset();
    }

    try {
      const result = await importPOIsFromExcel({ file, surveyId }).unwrap();
      setImportResult(result);
      setShowColumnMatching(true);
    } catch (error) {
      console.error("Failed to upload POIs:", error);
      alert("Failed to import Excel file. Please try again.");
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleColumnMatchingNext = (userMappings: Record<string, string>) => {
    if (!importResult) return;

    const headerMapping = Object.entries(userMappings).map(
      ([header, matchedAttribute]) => ({ header, matchedAttribute }),
    );

    parsePOIs({
      unParsedColumns: importResult.unParsedColumns,
      headerMapping,
    });
  };

  // Handle parse result — keyed on requestId so it fires for every new call
  useEffect(() => {
    if (
      parseResult.isSuccess &&
      parseResult.data &&
      parseResult.requestId !== lastParsedRequestId.current
    ) {
      lastParsedRequestId.current = parseResult.requestId;
      const data = parseResult.data;

      const poisArray =
        data?.geocodedPOIs ??
        data?.poiDocs ??
        data?.pois ??
        (Array.isArray(data) ? data : []);

      const errors = data?.geocodingErrors ?? data?.errors ?? [];

      setParsedPOIsData(data);
      setImportSummary({
        toImport: poisArray.length,
        notImported: errors.length,
        totalProperties: poisArray.length + errors.length,
        geocodingErrors: errors.length,
      });

      isTransitioningToRowReport.current = true;
      setShowColumnMatching(false);
      setTimeout(() => {
        isTransitioningToRowReport.current = false;
        setShowRowReport(true);
      }, 300);
    }
    if (
      parseResult.isError &&
      parseResult.requestId !== lastParsedRequestId.current
    ) {
      lastParsedRequestId.current = parseResult.requestId;
      setShowColumnMatching(false);
      alert("Failed to parse POIs. Please try again.");
    }
  }, [
    parseResult.isSuccess,
    parseResult.isError,
    parseResult.data,
    parseResult.requestId,
  ]);

  const handleRowReportImport = async () => {
    if (!parsedPOIsData) return;

    const poisArray =
      parsedPOIsData?.geocodedPOIs ??
      parsedPOIsData?.poiDocs ??
      parsedPOIsData?.pois ??
      (Array.isArray(parsedPOIsData) ? parsedPOIsData : []);

    if (poisArray.length === 0) return;

    const existingCount = pois.length;

    const formattedPOIs = poisArray.map((item: any, index: number) => ({
      categoryName: item.categoryName ?? "",
      addressLine1: item.addressLine1 ?? item.address ?? "",
      addressLine2: item.addressLine2 ?? undefined,
      city: item.city ?? "",
      state: item.state ?? "",
      postalCode: item.postalCode ?? item.zip ?? undefined,
      latitude: item.latitude ?? undefined,
      longitude: item.longitude ?? undefined,
      locationNotes: item.locationNotes ?? undefined,
      isHidden: false,
      order: existingCount + index,
    }));

    withLoading(
      async () =>
        await bulkImportPOIs({ surveyId, pois: formattedPOIs }).unwrap(),
      { successText: "POIs successfully imported!" },
    )
      .then((result: any) => {
        if (result?.importedPOIs?.length) {
          dispatch(setPOIs(result.importedPOIs));
        }

      })
      .catch((error: unknown) => {
        console.error("Failed to bulk import POIs:", error);
      });

    handleModalClose();
  };

  const handleModalClose = () => {
    setShowRowReport(false);
    setShowColumnMatching(false);
    setImportResult(null);
    setImportSummary(null);
    setParsedPOIsData(null);
  };

  const handleColumnMatchingClose = () => {
    if (!isTransitioningToRowReport.current) {
      handleModalClose();
    }
  };

  if (!surveyId) {
    return (
      <div className="h-full flex flex-col bg-white">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h3 className="text-lg font-medium text-slate-900 mb-2">
              No Survey Selected
            </h3>
            <p className="text-slate-500">
              Please select or create a survey to manage Points of Interest
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="h-full flex flex-col bg-white">
        {/* Header / Actions */}
        <div className="px-4 pb-4 border-b border-slate-200">
          <div className="flex items-center justify-end gap-3">
            <EmeraldButton
              id="SC-CreateNewPOI"
              icon="add"
              label="New Point of Interest"
              variant={EmeraldButtonVariant.TextButton}
              onClick={handleAddPoi}
            />
            <EmeraldButton
              icon="cloud_upload"
              iconFillType="outlined"
              label={isUploading ? "Uploading..." : "Upload Point of Interest from Excel"}
              id="SC-UploadPOIFromExcel"
              onClick={handleUploadFromExcel}
              disabled={isUploading}
            />
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
              accept=".xlsx,.xls"
            />
          </div>
        </div>

        {/* POI List */}
        <div className="flex-1 overflow-y-auto">
          {pois.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <EmptyState
                title="No Points of Interest yet"
                description="Points of Interest you add will appear here."
                status="no_data"
                position="center"
              />
            </div>
          ) : (
            <DndContext {...DndContextProps}>
              <SortableContext {...SortableContextProps}>
                <div className="divide-y divide-divider-4">
                  {pois.map((poi, mapIndex) => {
                    const visiblePOIsBeforeThis = pois
                      .slice(0, mapIndex)
                      .filter((p) => !p.isHidden).length;
                    return (
                      <PoiListItem
                        key={poi.extId}
                        poi={poi}
                        index={visiblePOIsBeforeThis}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      <POIColumnMatchingModal
        isOpen={showColumnMatching}
        onClose={handleColumnMatchingClose}
        onNext={handleColumnMatchingNext}
        importResult={importResult}
        isProcessing={parseResult.isLoading}
      />

      <RowReportModal
        isOpen={showRowReport}
        onClose={handleModalClose}
        onImport={handleRowReportImport}
        importData={importSummary}
        isImporting={isImporting}
        entityName="Points of Interest"
      />
    </>
  );
};

export default PoisListDrawer;
